/**
 * Content-script-side media upload helper. Extracted from
 * wa-tools-notifier-handlers.ts so both the dispatcher AND the
 * waGetMessageMediaTool thunk can use it without creating a circular
 * import (thunks → notifier-handlers → thunks).
 *
 * Uploads a DOM `File` to the backend's signed-GCS multipart endpoint.
 * Returns the signed URL + metadata, OR null when the upload failed.
 */

import { getStore } from "@common/state/state-provider";
import { appConfig } from "@common/config/app.config";

export interface UploadResponse {
  mediaUrl: string;
  fileName: string;
  fileType: string;
  fileSize: number;
  /** ISO timestamp when the signed download URL expires. Absent for legacy
   * (publicUrl) responses, present whenever the backend issued a signed GET. */
  expiresAt?: string;
}

export async function uploadMediaFile(
  mediaFile: File | undefined | null,
): Promise<UploadResponse | undefined | null> {
  if (!mediaFile) {
    return null;
  }

  const accessToken: string | undefined = (getStore().getState() as { UserReducer?: { accessToken?: string } })?.UserReducer?.accessToken;

  const form = new FormData();
  form.append('cmd', 'upload-mcp-media');
  form.append('file', mediaFile, mediaFile.name || 'media');

  const url = `${appConfig.API_ENDPOINT}/actions`;
  console.log('[uploadMediaFile] POST', url, 'auth?', !!accessToken, 'fileSize', mediaFile.size, 'mime', mediaFile.type);
  let res: Response;
  try {
    res = await fetch(url, {
      method: 'POST',
      body: form,
      headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
    });
  } catch (e: unknown) {
    const message = e instanceof Error ? e.message : String(e);
    console.warn('[uploadMediaFile] fetch threw', message);
    throw e;
  }
  console.log('[uploadMediaFile] response', { status: res.status, statusText: res.statusText, ok: res.ok, contentType: res.headers.get('content-type'), contentLength: res.headers.get('content-length') });
  const text = await res.text();
  console.log('[uploadMediaFile] body len', text.length, 'first 200', text.slice(0, 200));
  if (!res.ok) {
    console.warn('[uploadMediaFile] upload failed', res.status, text);
    return null;
  }
  let data: { mediaUrl: string; expiresAt: string; fileName: string; fileType: string; fileSize: number; error?: string };
  try {
    data = JSON.parse(text);
  } catch {
    console.warn('[uploadMediaFile] JSON parse failed; body was empty or not JSON. Status was', res.status);
    return null;
  }
  if (data.error || !data.mediaUrl) {
    console.warn('[uploadMediaFile] backend returned no mediaUrl', data);
    return null;
  }

  return {
    mediaUrl: data.mediaUrl,
    fileName: data.fileName,
    fileType: data.fileType,
    fileSize: data.fileSize,
    expiresAt: data.expiresAt,
  };
}
